/**
 * Email-to-book offerings from the owner's November 2026 copy: in-home art
 * therapy and packages & bundles. None of these are online-bookable catalog
 * rows (that is services.js); every call to action is an email to the
 * practice address.
 *
 * Free groups, workshops and private group experiences are owner-editable
 * content since CMS-04 (contract v1.2 §3.13): the backend serves them and
 * contentSeed/ holds the bundled copy (see data/content.js, useContent).
 */
import config from "../config";
import { PRACTICE, lateCancelFeeText } from "./practice";

/** mailto: link to the practice address with a subject line. */
export function emailHref(subject) {
  return `mailto:${config.business.contactEmail}?subject=${encodeURIComponent(subject)}`;
}

/**
 * The payment line used on every paid email-to-book offering (her round-2
 * answers 1.1 and 1.2: transfer or cheque before or at the session; bank
 * details by email on request).
 */
export function paymentMethodText(email = config.business.contactEmail) {
  return `Payment by bank transfer or cheque, before or at your session. Email ${email} for bank transfer details.`;
}

/**
 * The owner's cancellation lines for in-home sessions and bundles (doc 92),
 * with her November 2026 fee (50% of the session fee). Bundles leave out
 * "non-refundable": stopping a bundle can be refunded (BUNDLE_CANCELLATION_TEXT).
 */
export function sessionCancellationText(hours = PRACTICE.cancellationWindowHours, { nonRefundable = true } = {}) {
  const text = `Sessions must be canceled or rescheduled at least ${hours} hours in advance to avoid a cancellation fee. Late cancellations and no-shows may be charged ${lateCancelFeeText()}. A one-time waiver is available for first-time no-shows.`;
  return nonRefundable ? `${text} All payments are non-refundable once processed.` : text;
}

/* ------------------------------------------------------------------ */
/* In-home art therapy (doc 74-141)                                    */
/* ------------------------------------------------------------------ */

export const IN_HOME = {
  title: "In-Home Art Therapy Sessions: Nassau",
  intro: [
    "Prefer to be seen in your own space? I offer in-home art therapy sessions within Nassau for clients who find it easier to engage from a familiar environment. A $20 gas fee and a $15 materials fee apply per session in addition to the session rate.",
    "Please note that clients are responsible for providing a safe, quiet, and private space suitable for a therapy session. In-home sessions are available for art therapy only. All in-home clients must complete an intake session before their first in-home appointment.",
  ],
  services: [
    {
      id: "in-home-intake",
      title: "In-Home Intake Session",
      duration: "75 min",
      price: 195,
      // Round 2: she kept the default that the in-home fees also apply to the
      // intake, and changed them to a $20 gas fee plus a $15 materials fee.
      gasFee: 20,
      materialsFee: 15,
      short: "Our first meeting, in your space. We'll cover your reasons for seeking art therapy, your goals, what support looks like for you, and so on.",
      description: [
        "The intake session is your first official step into therapy. During this session, we’ll explore your reasons for seeking support, your goals, and any relevant history. It’s a collaborative space designed to help us build a foundation for your care, whether you’re interested in talk therapy, art therapy, or both.",
        "This is a one-time session with a flat fee of $195, separate from ongoing session costs. Sliding scale available upon request.",
      ],
    },
    {
      id: "in-home-art-therapy",
      title: "In-Home Art Therapy",
      duration: "50 min",
      price: 195,
      gasFee: 20,
      materialsFee: 15,
      short: "All the depth of art therapy, in the comfort of your home. We'll create, reflect, and process together using whatever materials work for your space. No art experience needed. Just a quiet corner and a willingness to show up.",
      description: [
        "Art therapy, in the comfort of your own space. We’ll create, reflect, and process together using whatever materials work best for your environment. No art experience needed. Just a quiet corner and a willingness to show up.",
        "Please ensure you have a safe, quiet, and private space available for our session. In-home sessions are available for art therapy only. A completed intake session is required before your first in-home art therapy appointment.",
      ],
    },
    {
      id: "in-home-extended-art-therapy",
      title: "In-Home Extended Art Therapy",
      duration: "85 min",
      price: 225,
      gasFee: 20,
      materialsFee: 15,
      short: "For clients who want more time and space to go deeper. Extended in-home sessions are ideal for those who find that the creative process opens up slowly and benefits from a longer window.",
      description: [
        "For those who find the creative process opens up slowly and benefits from more room. Extended in-home sessions offer additional time to go deeper, move at your own pace, and let the work unfold fully.",
        "Please ensure you have a safe, quiet, and private space available for our session. In-home sessions are available for art therapy only. A completed intake session is required before your first in-home appointment.",
      ],
    },
  ],
};

/** "$195 + $20 gas fee + $15 materials fee" (each fee shown only when set). */
export function inHomePriceText({ price, gasFee, materialsFee }) {
  const parts = [`$${price}`];
  if (gasFee) parts.push(`$${gasFee} gas fee`);
  if (materialsFee) parts.push(`$${materialsFee} materials fee`);
  return parts.join(" + ");
}

/* ------------------------------------------------------------------ */
/* Packages & bundles (her round-2 text, answer 3.4, Nov 2026)          */
/* ------------------------------------------------------------------ */

/** The regular price of one 50-minute session, for per-session comparisons. */
export const REGULAR_SESSION_PRICE = 170;

/** Full prices used when a bundle is stopped early (her November 2026 policy). */
export const BUNDLE_FULL_PRICES = Object.freeze({ intake: 195, session: REGULAR_SESSION_PRICE });

/**
 * Her four bundles, word for word. `intro` is shown; `more` sits behind her
 * "Read More" break; the card shows "Investment: $<price>" and `split`
 * after it. `short` is the italic lead line. `sessions` counts every
 * session in the bundle (the intake included) and `includes` says what
 * they are; both feed the per-session price on Home.
 */
export const BUNDLES = [
  {
    id: "virtual-student-bundle",
    title: "Virtual Student Therapy Bundle",
    price: 525,
    sessions: 6,
    includes: "Intake + five 45-minute virtual sessions",
    short: "A supportive introduction to therapy, designed with students in mind.",
    intro: [
      "This bundle includes an initial intake session followed by five 45-minute virtual therapy sessions. Together, we can explore the experiences and concerns affecting your well-being, develop a better understanding of your emotions, and identify practical ways to navigate the challenges of student life.",
    ],
    more: [
      "The bundle offers an opportunity to begin therapeutic work at a manageable pace without an initial commitment to ongoing therapy. Unlike Safe Space Sessions, this bundle includes an intake and a personalized plan for your care.",
      "At the end of the five sessions, we can reflect on your progress and discuss whether additional sessions or other support may be helpful.",
    ],
    split: "Pay in full or split into two payments of $263 and $262. The second payment is due before Session 3.",
  },
  {
    id: "virtual-student-continuation-bundle",
    title: "Virtual Student Therapy Continuation Bundle",
    price: 325,
    sessions: 5,
    includes: "Five more 45-minute virtual sessions",
    short: "Ready to continue your therapeutic work? This bundle offers an opportunity to build on the progress you’ve already made.",
    intro: [],
    more: [
      "Available to students who have completed the Virtual Student Therapy Bundle, this package includes five additional 45-minute virtual therapy sessions at a reduced bundled rate. No new intake is required, provided continued care remains clinically appropriate.",
      "At the end of the five sessions, we can reflect on your progress and discuss any additional support that may be helpful.",
    ],
    split: "Pay in full or split into two payments of $163 and $162. The second payment is due before Session 3.",
  },
  {
    id: "virtual-adult-bundle",
    title: "Virtual Adult Therapy Bundle",
    price: 895,
    sessions: 6,
    includes: "Intake + five 50-minute sessions",
    short: "Consistent, ongoing support for adults ready to commit to their healing.",
    intro: [
      "This bundle includes an initial intake session followed by five 50-minute individual therapy sessions at a reduced bundled rate. It offers an opportunity to invest in your emotional wellbeing and engage in consistent therapeutic support.",
    ],
    more: [
      "Together, we can explore your experiences, deepen emotional understanding, and develop practical tools to navigate life’s challenges.",
      "At the end of the five sessions, we can reflect on your progress and discuss whether additional support may be helpful.",
    ],
    split: "Pay in full or split into two payments of $448 and $447. The second payment is due before Session 3.",
  },
  {
    id: "virtual-adult-continuation-bundle",
    title: "Virtual Adult Therapy Continuation Bundle",
    price: 640,
    sessions: 5,
    includes: "Five more 50-minute sessions",
    short: "Keep the momentum going.",
    intro: [
      "Once you’ve completed the Virtual Adult Therapy Bundle, this continuation package offers five additional 50-minute individual therapy sessions at a discounted bundled rate. It’s an opportunity to build on the work you’ve already started and continue developing the tools and understanding that support your emotional wellbeing.",
    ],
    more: [
      "Available to adults who have completed the initial Virtual Adult Therapy Bundle, provided continued care remains clinically appropriate.",
      "At the end of the five sessions, we can reflect on your progress and discuss whether additional support may be helpful.",
    ],
    split: "Pay in full or split into two payments of $320.",
  },
];

/** Her "Student Eligibility" block (replaces the old full-time 18+ default). */
export const STUDENT_ELIGIBILITY = [
  "Student pricing is available to students enrolled in primary school, junior high, senior high, college, or university. College and university graduates may continue to access student pricing for up to one year after graduation.",
  "Students under 18 require parent or guardian consent before services begin. Proof of enrollment or a student ID may be requested.",
];

/** Her round-2 note (section 6): paying per session is fine too. */
export const BUNDLE_PER_SESSION_TEXT = "If you need to pay per session instead, that’s fine too.";

/** About $88 a session: the bundle price over its sessions, intake included. */
export function bundlePerSession({ price, sessions }) {
  return Math.round(price / sessions);
}

/**
 * Stopping a bundle (her November 2026 correction; replaces "non-refundable
 * once the first session has been attended").
 */
export const BUNDLE_CANCELLATION_TEXT = `You can stop a bundle at any time. You then pay the full price of the services you received: $${BUNDLE_FULL_PRICES.intake} for the intake plus $${BUNDLE_FULL_PRICES.session} for each session attended. If you paid more than that, I refund the difference; if you paid less, the difference is owed.`;
